import { db, type LookupItem } from './db';

const API_BASE = import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? '';
const LOOKUP_CACHE_DAILY = 24 * 60 * 60 * 1000;

export function getCurrentFieldStudyYear() {
  const today = new Date();
  const calendarYear = today.getFullYear();
  const month = today.getMonth();

  return month >= 9 ? calendarYear + 1 : calendarYear;
}

function getAuthHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function refreshOfflineLookups() {
  const lastDownloaded = await db.meta.get('lookupsLastDownloaded');
  if (!lastDownloaded?.value) {
    return false;
  }

  const downloadedAt = new Date(lastDownloaded.value).getTime();
  if (!Number.isFinite(downloadedAt)) {
    return false;
  }

  const age = Date.now() - downloadedAt;
  return age < LOOKUP_CACHE_DAILY;
}

export async function downloadLookupsForOffline(token?: string) {
  const refreshLookups = await refreshOfflineLookups();
  if (refreshLookups) {
    const existingCount = await db.lookups.count();
    if (existingCount > 0) {
      console.log('Offline lookups are already current. Skipping lookup download.');

      return {
        ok: true,
        count: existingCount,
        cached: true,
      };
    }
  }

  const res = await fetch(`${API_BASE}/psapi/Lookup/getAllLookups`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (!res.ok) {
    throw new Error(`Failed to download lookup data: ${res.status}`);
  }
  const json = await res.json();

  const lookupData = json?.data ?? json;

  const rowsToSave: LookupItem[] = [];

  const lookupMappings = [
    // sites
    { key: 'bendRiverMile', name: 'bendRiverMile' },
    { key: 'bendSelections', name: 'bendSelections' },
    { key: 'chutes', name: 'chutes' },
    { key: 'fieldOffices', name: 'fieldOffices' },
    { key: 'fieldOfficeSegments', name: 'fieldOfficeSegments' },
    { key: 'projects', name: 'projects' },
    { key: 'reach', name: 'reach' },
    { key: 'sampleUnitTypes', name: 'sampleUnitTypes' },
    { key: 'seasons', name: 'seasons' },
    { key: 'segments', name: 'segments' },
    { key: 'years', name: 'years' },
    // telemetry
    { key: 'frequencyId', name: 'frequencyId' },
    { key: 'spawnBehavior', name: 'spawnBehavior' },
    { key: 'macros', name: 'macros' },
    { key: 'mesos', name: 'mesos' },
    { key: 'positionConfidence', name: 'positionConfidence' },
    // search effort
    { key: 'searchTypeCodes', name: 'searchTypeCodes' },
    // missouri river
    { key: 'gearCodes', name: 'gearCodes' },
    { key: 'filteredGearCodes', name: 'filteredGearCodes' },
    { key: 'gearTypes', name: 'gearTypes' },
    { key: 'macroMesos', name: 'macroMesos' },
    { key: 'microHabitats', name: 'microHabitats' },
    { key: 'microStructures', name: 'microStructures' },
    { key: 'u6Options', name: 'u6Options' },
    { key: 'u7Options', name: 'u7Options' },
    { key: 'microSetSite', name: 'microSetSite' },
    { key: 'setSite1Options', name: 'setSite1Options' },
    { key: 'setSite2Options', name: 'setSite2Options' },
    { key: 'setSite3Options', name: 'setSite3Options' },
    { key: 'structureFlows', name: 'structureFlows' },
    { key: 'structureMods', name: 'structureMods' },
    { key: 'subsampleTypes', name: 'subsampleTypes' },
    // fish
    { key: 'fishCodes', name: 'fishCodes' },
    { key: 'fishStructures', name: 'fishStructures' },
    { key: 'floyTagPrefixes', name: 'floyTagPrefixes' },
    { key: 'lengthTypes', name: 'lengthTypes' },
    { key: 'markRecaptureOptions', name: 'markRecaptureOptions' },
  ];

  for (const mapping of lookupMappings) {
    const rows = lookupData?.[mapping.key] ?? [];

    for (const row of rows) {
      rowsToSave.push({
        lookupName: mapping.name,
        code: row.code,
        label: row.description,
        raw: row,
        updatedAt: new Date().toISOString(),
      });
    }
  }

  await db.lookups.clear();

  if (rowsToSave.length > 0) {
    await db.lookups.bulkAdd(rowsToSave);
  }

  await db.meta.put({
    key: 'lookupsLastDownloaded',
    value: new Date().toISOString(),
  });

  return { ok: true, count: rowsToSave.length };
}

export async function downloadSitesForOffline(token?: string, userRole?: any) {
  if (!userRole) {
    throw new Error('Cannot download offline Sites because the user role data is missing');
  }

  const { id: userRoleId } = userRole;
  const fieldStudyYear = getCurrentFieldStudyYear();
  const response = await fetch(`${API_BASE}/psapi/Offline/sites?id=${encodeURIComponent(userRoleId)}`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (response.status === 401) {
    throw new Error('Offline setup authorization expired while downloading sites');
  }
  if (!response.ok) {
    throw new Error(`Failed to download offline sites: ${response.status}`);
  }

  const json = await response.json();
  const data = json?.data ?? json;

  const sites = Array.isArray(data?.sites) ? data.sites : [];
  const apiFieldYear = Number(data?.fieldYear);
  if (Number.isFinite(apiFieldYear) && apiFieldYear !== fieldStudyYear) {
    console.warn(`Offline Sites field year mismatch. UI: ${fieldStudyYear}, API: ${apiFieldYear}`);
  }

  const existingSites = await db.sites.toArray();
  const downloadedClientIds = existingSites
    .filter((site: any) => site?._status !== 'queued' && site?._status !== 'draft')
    .map((site: any) => site?.clientId)
    .filter(Boolean);

  if (downloadedClientIds.length > 0) {
    await db.sites.bulkDelete(downloadedClientIds);
  }

  const sitesToSave = sites.map((site: any) => {
    const siteId = Number(site?.siteId ?? site?.site_id);
    const siteFid = site?.siteFid ?? site?.site_fid ?? null;

    return {
      ...site,
      clientId: `server-site-${siteId}`,
      serverId: siteId,
      siteId,
      site_id: siteId,
      siteFid,
      site_fid: siteFid,
      projectId: site?.projectId ?? site?.project_id,
      project_id: site?.project_id ?? site?.projectId,
      segmentId: site?.segmentId ?? site?.segment_id,
      segment_id: site?.segment_id ?? site?.segmentId,
      _status: 'synced',
      version: site?.version ?? 0,
      updatedAt: site?.lastUpdated ?? site?.last_updated ?? new Date().toISOString(),
    };
  });

  if (sitesToSave.length > 0) {
    await db.sites.bulkPut(sitesToSave);
  }

  await db.meta.put({
    key: 'sitesLastDownloaded',
    value: new Date().toISOString(),
  });

  await db.meta.put({
    key: 'sitesDownloadedYear',
    value: String(fieldStudyYear),
  });

  return {
    ok: true,
    year: fieldStudyYear,
    count: sitesToSave.length,
  };
}

export async function downloadDatasheetsForOffline(token?: string, userRole?: any) {
  if (!userRole) {
    throw new Error('Cannot download offline datasheets because the user role ID is missing');
  }

  const { id: userRoleId } = userRole;
  const fieldStudyYear = getCurrentFieldStudyYear();
  const response = await fetch(`${API_BASE}/psapi/Offline/draftDatasheets?id=${encodeURIComponent(userRoleId)}`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (response.status === 401) {
    throw new Error('Offline setup authorization expired while downloading draft datasheets');
  }
  if (!response.ok) {
    throw new Error(`Failed to download offline draft datasheets: ${response.status}`);
  }

  const json = await response.json();
  const data = json?.data ?? json;
  const apiFieldYear = Number(data?.fieldYear);
  if (Number.isFinite(apiFieldYear) && apiFieldYear !== fieldStudyYear) {
    console.warn(`Offline datasheet field year mismatch. UI: ${fieldStudyYear}, API: ${apiFieldYear}`);
  }

  const moriverRows = Array.isArray(data?.moriver) ? data.moriver : [];
  const searchRows = Array.isArray(data?.search) ? data.search : [];
  const fishRows = Array.isArray(data?.fish) ? data.fish : [];
  const suppRows = Array.isArray(data?.supplemental) ? data.supplemental : [];
  const procRows = Array.isArray(data?.procedure) ? data.procedure : [];
  const telemetryRows = Array.isArray(data?.telemetry) ? data.telemetry : [];

  await clearOldDownloadedDatasheets();

  const fishCountByMrId = new Map<string, number>();
  for (const fish of fishRows) {
    const mrId = fish?.mrId ?? fish?.mr_id;
    if (mrId != null) {
      const key = String(mrId);
      fishCountByMrId.set(key, (fishCountByMrId.get(key) ?? 0) + 1);
    }
  }

  const telemetryCountBySeId = new Map<string, number>();
  for (const telemetry of telemetryRows) {
    const seId = telemetry?.seId ?? telemetry?.se_id;
    if (seId != null) {
      const key = String(seId);
      telemetryCountBySeId.set(key, (telemetryCountBySeId.get(key) ?? 0) + 1);
    }
  }

  const moriverToSave = moriverRows.map((mr: any) => {
    const mrId = Number(mr?.mrId ?? mr?.mr_id);
    const mrFid = mr?.mrFid ?? mr?.mr_fid ?? null;
    const siteId = Number(mr?.siteId ?? mr?.site_id);

    return {
      ...mr,
      clientId: `server-moriver-${mrId}`,
      serverId: mrId,
      mrId,
      mr_id: mrId,
      mrFid,
      mr_fid: mrFid,
      siteId,
      site_id: siteId,
      fishCount: fishCountByMrId.get(String(mrId)) ?? 0,
      _status: 'synced',
      version: mr?.version ?? 0,
      updatedAt: mr?.lastUpdated ?? mr.last_updated ?? new Date().toISOString(),
    };
  });

  const searchToSave = searchRows.map((search: any) => {
    const seId = Number(search?.seId ?? search?.se_id);
    const seFid = search?.seFid ?? search?.se_fid ?? null;
    const siteId = Number(search?.siteId ?? search?.site_id);

    return {
      ...search,
      clientId: `server-search-${seId}`,
      serverId: seId,
      seId,
      se_id: seId,
      seFid,
      se_fid: seFid,
      siteId,
      site_id: siteId,
      telemetryCount: telemetryCountBySeId.get(String(seId)) ?? 0,
      _status: 'synced',
      version: search?.version ?? 0,
      updatedAt: search?.lastUpdated ?? search?.last_updated ?? new Date().toISOString(),
    };
  });

  const fishToSave = fishRows.map((fish: any) => {
    const fishId = Number(fish?.fid ?? fish?.fId ?? fish?.f_id);
    const fishFid = fish?.fFid ?? fish?.f_fid ?? fish?.ffid ?? null;
    const mrId = Number(fish?.mrId ?? fish?.mr_id);

    return {
      ...fish,
      clientId: `server-fish-${fishId}`,
      serverId: fishId,
      fid: fishId,
      fId: fishId,
      f_id: fishId,
      fFid: fishFid,
      f_fid: fishFid,
      mrId,
      mr_id: mrId,
      _status: 'synced',
      version: fish?.version ?? 0,
      updatedAt: fish?.lastUpdated ?? fish?.last_updated ?? new Date().toISOString(),
    };
  });

  const suppToSave = suppRows.map((supp: any) => {
    const suppId = Number(supp?.sid ?? supp?.sId ?? supp?.s_id);
    const fishId = Number(supp?.fid ?? supp?.fId ?? supp?.f_id);
    const fishFid = supp?.fFid ?? supp?.f_fid ?? null;
    const mrId = Number(supp?.mrId ?? supp?.mr_id);

    return {
      ...supp,
      clientId: `server-supplemental-${suppId}`,
      serverId: suppId,
      sid: suppId,
      sId: suppId,
      s_id: suppId,
      fid: fishId,
      fId: fishId,
      f_id: fishId,
      fFid: fishId,
      f_fid: fishId,
      mrId,
      mr_id: mrId,
      _status: 'synced',
      version: supp?.version ?? 0,
      updatedAt: supp?.lastUpdated ?? supp?.last_updated ?? new Date().toISOString(),
    };
  });

  const procToSave = procRows.map((proc: any) => {
    const procId = Number(proc?.id);
    const fishId = Number(proc?.fid ?? proc?.fId ?? proc?.f_id);
    const fishFid = proc?.fFid ?? proc?.f_fid ?? null;

    return {
      ...proc,
      clientId: `server-procedure-${procId}`,
      serverId: procId,
      id: procId,
      fid: fishId,
      fId: fishId,
      f_id: fishId,
      fFid: fishFid,
      f_fid: fishFid,
      _status: 'synced',
      version: proc?.version ?? 0,
      updatedAt: proc?.lastUpdated ?? proc?.last_updated ?? new Date().toISOString(),
    };
  });

  const telemetryToSave = telemetryRows.map((tel: any) => {
    const telemetryId = Number(tel?.tId ?? tel?.t_id);
    const telemetryFid = tel?.tFid ?? tel?.t_fid ?? null;
    const seId = Number(tel?.seId ?? tel?.se_id);
    const seFid = tel?.seFid ?? tel?.se_fid ?? null;

    return {
      ...tel,
      clientId: `server-telemetry-${telemetryId}`,
      serverId: telemetryId,
      tId: telemetryId,
      t_id: telemetryId,
      tFid: telemetryFid,
      t_fid: telemetryFid,
      seId,
      se_id: seId,
      seFid,
      se_fid: seFid,
      _status: 'synced',
      version: tel?.version ?? 0,
      updatedAt: tel?.lastUpdated ?? tel?.last_updated ?? new Date().toISOString(),
    };
  });

  if (moriverToSave.length > 0) {
    await db.moriver.bulkPut(moriverToSave);
  }
  if (searchToSave.length > 0) {
    await db.search.bulkPut(searchToSave);
  }
  if (fishToSave.length > 0) {
    await db.fish.bulkPut(fishToSave);
  }
  if (suppToSave.length > 0) {
    await db.supplemental.bulkPut(suppToSave);
  }
  if (procToSave.length > 0) {
    await db.procedure.bulkPut(procToSave);
  }
  if (telemetryToSave.length > 0) {
    await db.telemetry.bulkPut(telemetryToSave);
  }

  const totalCount =
    moriverToSave.length +
    searchToSave.length +
    fishToSave.length +
    suppToSave.length +
    procToSave.length +
    telemetryToSave.length;

  await db.meta.put({
    key: 'datasheetsLastDownloaded',
    value: new Date().toISOString(),
  });
  await db.meta.put({
    key: 'datasheetsDownloadedYear',
    value: String(fieldStudyYear),
  });

  return {
    ok: true,
    year: fieldStudyYear,
    count: totalCount,
    counts: {
      moriver: moriverToSave.length,
      search: searchToSave.length,
      fish: fishToSave.length,
      supplemental: suppToSave.length,
      procedure: procToSave.length,
      telemetry: telemetryToSave.length,
    },
  };
}

async function clearOldDownloadedDatasheets() {
  const clearSyncedRows = async (table: any) => {
    const rows = await table.toArray();
    const clientIds = rows
      .filter((row: any) => row?._status !== 'queued' && row?._status !== 'draft')
      .map((row: any) => row.clientId)
      .filter(Boolean);

    if (clientIds.length > 0) {
      await table.bulkDelete(clientIds);
    }
  };
  await clearSyncedRows(db.moriver);
  await clearSyncedRows(db.search);
  await clearSyncedRows(db.telemetry);
  await clearSyncedRows(db.fish);
  await clearSyncedRows(db.supplemental);
  await clearSyncedRows(db.procedure);
}

export async function getLookupOptions(lookupName: string) {
  const rows = await db.lookups.where('lookupName').equals(lookupName).toArray();

  // Lookup tables that don't have code/description field names or have additional fields
  const customDataObjArr = [
    'bendRiverMile',
    'fieldOfficeSegments',
    'years',
    'filteredGearCodes',
    'macroMesos',
    'microHabitats',
    'microSetSite',
    'fishCodes',
  ];

  if (customDataObjArr.includes(lookupName)) {
    return rows.map((row: any) => row.raw);
  }

  // Default
  return rows.map((row: any) => ({
    code: row.code,
    description: row.label,
  }));
}

export async function downloadPallidIdDataForOffline(token?: string) {
  const response = await fetch(`${API_BASE}/psapi/DataEntry/getAllPallidIdOfflineData`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (response.status === 401) {
    throw new Error('Authorization expired while downloading Pallid ID data.');
  }

  if (!response.ok) {
    throw new Error(`Failed to download Pallid ID data: ${response.status}`);
  }

  const json = await response.json();
  const data = json?.data ?? json;
  const stockedJuveniles = data?.stockedJuveniles ?? [];
  const recaptureInfo = data?.recaptureInfo ?? [];
  const normalizeTag = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toUpperCase();
  const pallidByTag = new Map<string, any>();
  const getOrCreate = (tagValue: unknown) => {
    const tag = normalizeTag(tagValue);

    if (!tag) {
      return null;
    }

    if (!pallidByTag.has(tag)) {
      pallidByTag.set(tag, {
        tagnumber: tag,
        stockedJuvenileInfo: [],
        recaptureInfo: [],
        updatedAt: new Date().toISOString(),
      });
    }
    return pallidByTag.get(tag);
  };

  for (const row of stockedJuveniles) {
    const entry = getOrCreate(row?.pitTag);

    if (!entry) continue;

    entry.stockedJuvenileInfo.push({
      hatchery: row?.hatchery ?? null,
      stockSite: row?.stockSite ?? null,
      yearClass: row?.yearClass ?? null,
      cwt: row?.cwt ?? null,
      scute: row?.scute ?? null,
      er: row?.er ?? null,
      el: row?.el ?? null,
    });
  }

  for (const row of recaptureInfo) {
    const entry = getOrCreate(row?.tagnumber);

    if (entry) {
      entry.recaptureInfo.push(row);
    }
  }

  const rows = Array.from(pallidByTag.values());

  await db.pallidId.clear();

  if (rows.length > 0) {
    await db.pallidId.bulkPut(rows);
  }

  await db.meta.put({
    key: 'pallidIdLastDownloaded',
    value: new Date().toISOString(),
  });

  return {
    ok: true,
    count: rows.length,
  };
}
